import { Alert, Box, Button, Menu, MenuItem, Modal, TextField } from "@mui/material";
import SelectDropDown from "../SelectDropdown/SelectDropDown";
import { DropdownCheck, DropdownClear } from "../../Icons/Icons";
import { useState } from "react";

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterConfig {
  type: string;
  label: string;
  options: FilterOption[];
  selectedValue?: string;
  onFilterAdd: (value: string) => void;
  onFilterClear: () => void;
  hasCreateNewFilter?: boolean;
  hasEditableFilters?: boolean;
  createFilter?: (filter: string) => void;
  deleteFilter?: (filterId: string, filterValue: string) => void;
  editFilter?: (filterId: string, filterValue: string) => void;
  isNumeric?: boolean;
}

interface GridNavigationProps {
  filters: FilterConfig[];
  onAddItem?: (type: string) => void;
  addMenuItems?: { label: string; value: string }[];
  baseRoute: string;
  showAddMenu?: boolean;
}

const GridNavigation = ({ filters, onAddItem, addMenuItems, baseRoute, showAddMenu = false }: GridNavigationProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [modalFilterValue, setModalFilterValue] = useState("");
  const [filterToDelete, setFilterToDelete] = useState("");
  const [filterToUpdate, setFilterToUpdate] = useState("");
  const [updatedFilterValue, setUpdatedFilterValue] = useState("");
  const [currentFilterType, setCurrentFilterType] = useState<string>("");

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (showAddMenu) {
      setAnchorEl(event.currentTarget);
    } else if (onAddItem) {
      onAddItem('photo'); // Default action for direct click
    }
  };

  const handleClose = () => setAnchorEl(null);

  const handleModalOpen = (filterType: string) => {
    setCurrentFilterType(filterType);
    if (filterType === 'edit') {
      setEditModalOpen(true);
    } else {
      setDeleteModalOpen(true);
    }
  };

  const handleModalClose = (filterType: string) => {
    if (filterType === 'edit') {
      setEditModalOpen(false);
    } else {
      setDeleteModalOpen(false);
    }
  };

  const handleDeleteItem = () => {
    const filter = filters.find(f => f.type === currentFilterType);
    if (filter?.deleteFilter) {
      filter.deleteFilter(filterToDelete, modalFilterValue);
      handleModalClose(currentFilterType);
      filter.onFilterClear();
    }
  };

  const handleUpdateItem = () => {
    const filter = filters.find(f => f.type === currentFilterType);
    if (filter?.editFilter) {
      filter.editFilter(filterToUpdate, updatedFilterValue);
      handleModalClose('edit');
    } else {
      console.error("No edit filter function provided for filter type:", currentFilterType);
    } 
  };

  const getDropdownClassName = (selectedValue?: string) =>
    selectedValue !== "undefined" ? "active" : "";

  const addItemMenu = addMenuItems && (
    <Menu
      classes={{ list: "main-list grid-list" }}
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
    >
      {addMenuItems.map(item => (
        <MenuItem 
          key={item.value} 
          onClick={() => {
            onAddItem?.(item.value);
            handleClose();
          }}
        >
          {item.label}
        </MenuItem>
      ))}
    </Menu>
  );

  return (
    <nav className="nav">
      {onAddItem && (
        <ul className="submenu">
          <li>
            <Button className="button button-blue" onClick={handleClick}>
              Add +
            </Button>
            {showAddMenu && addItemMenu}
          </li>
        </ul>
      )}
      <ul>
        {filters.map((filter) => (
          <li key={filter.type}>
            <SelectDropDown
              options={[
                { label: filter.label, value: "undefined" },
                ...filter.options,
              ]}
              startAdornment={<DropdownCheck />}
              endAdornment={
                <span onClick={filter.onFilterClear}>
                  <DropdownClear />
                </span>
              }
              selectedOption={filter.selectedValue}
              onChange={(evt) => filter.onFilterAdd(evt.target.value)}
              createFilter={filter.createFilter}
              deleteFilter={filter.deleteFilter ? 
                (filterId: string, filterValue: string) => {
                  setModalFilterValue(filterValue);
                  setFilterToDelete(filterId);
                  handleModalOpen(filter.type);
                } : undefined}
              editFilter={filter.editFilter ?
                (filterId: string, filterValue: string) => {
                  setModalFilterValue(filterValue);
                  setFilterToUpdate(filterId);
                  setCurrentFilterType(filter.type);
                  setEditModalOpen(true);
                } : undefined}
              hasCreateNewFilter={filter.hasCreateNewFilter}
              hasEditableFilters={filter.hasEditableFilters}
              className={getDropdownClassName(filter.selectedValue)}
            />
          </li>
        ))}
      </ul>
      <Modal className="modal" open={deleteModalOpen} onClose={() => handleModalClose(currentFilterType)}>
        <Box className="delete-locator-property-modal-box">
          <Alert className="warning-modal" variant="filled" severity="warning">
            Warning: You may lose data!
          </Alert>
          <p>
            You are removing {currentFilterType} "{modalFilterValue}" from the {currentFilterType} labels. 
            All images with this label will lose their {currentFilterType} label.
          </p>
          <Button onClick={() => handleModalClose(currentFilterType)}>Cancel</Button>
          <Button onClick={handleDeleteItem}>Delete</Button>
        </Box>
      </Modal>
      <Modal className="modal" open={editModalOpen} onClose={() => handleModalClose('edit')}>
        <Box className="delete-locator-property-modal-box">
          <Alert className="warning-modal" variant="filled" severity="warning">
            Warning: You might update data that is linked to multiple items!
          </Alert>
          <p>
            You are updating {currentFilterType}: "{modalFilterValue}" to:
          </p>
          <div className="new-filter-textfield-container">
            <TextField 
              id="new-filter-textfield" 
              onKeyDown={(e) => e.stopPropagation()} 
              value={updatedFilterValue} 
              onChange={(e) => setUpdatedFilterValue(e.target.value)} 
            />
          </div>
          <Button onClick={() => handleModalClose('edit')}>Cancel</Button>
          <Button onClick={handleUpdateItem}>Update</Button>
        </Box>
      </Modal>
    </nav>
  );
};

export default GridNavigation;
